import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormField, FormGrid, HpButton, HpCardHeader, HpTag } from '@hp/ui';
import { ProjectInputSchema, type ProjectInput } from '@hp/domain';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { LocationPicker } from './location-picker';
import { invalidateProjects, type ProjectInfo } from './queries';
import { toIntOrNull, toNumOrNull } from './field';
import { ValidationErrors } from './validation-errors';
import { zodIssueList } from './validation';

const DISTANCE_FIELDS = [
  'minutesShops',
  'minutesRestaurant',
  'minutesPublicTransport',
  'minutesSchool',
  'minutesHospital',
  'minutesSport',
] as const;
type DistanceKey = (typeof DISTANCE_FIELDS)[number];

/** Fields of ProjectInput, in the order validation issues should name them (`form.fields.*`). */
const PROJECT_FIELDS = [
  'name',
  'description',
  'address',
  'websiteUrl',
  'lat',
  'lng',
  'yearOfCompletion',
  'numberOfUnits',
  ...DISTANCE_FIELDS,
] as const;

const str = (v: number | null) => (v === null ? '' : String(v));

/** Mount with key={project.id} — field state initializes from props once. */
export function ProjectForm({ project, unitCountFallback }: { project: ProjectInfo; unitCountFallback: number }) {
  const { t } = useTranslation(['projects', 'common']);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [address, setAddress] = useState(project.address ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(project.websiteUrl ?? '');
  const [lat, setLat] = useState(str(project.lat));
  const [lng, setLng] = useState(str(project.lng));
  const [year, setYear] = useState(str(project.yearOfCompletion));
  const [numberOfUnits, setNumberOfUnits] = useState(str(project.numberOfUnits));
  const [distances, setDistances] = useState<Record<DistanceKey, string>>({
    minutesShops: str(project.minutesShops),
    minutesRestaurant: str(project.minutesRestaurant),
    minutesPublicTransport: str(project.minutesPublicTransport),
    minutesSchool: str(project.minutesSchool),
    minutesHospital: str(project.minutesHospital),
    minutesSport: str(project.minutesSport),
  });
  const [savedNotice, setSavedNotice] = useState(false);
  const [issues, setIssues] = useState<string[]>([]);
  const canEdit = useCan()('projects.edit');

  const save = useMutation(
    trpc.projects.update.mutationOptions({
      onSuccess: async () => {
        await invalidateProjects();
        setSavedNotice(true);
        window.setTimeout(() => setSavedNotice(false), 1800);
      },
    }),
  );

  function submit() {
    const candidate: ProjectInput = {
      name: name.trim(),
      address: address.trim() === '' ? null : address.trim(),
      websiteUrl: websiteUrl.trim() === '' ? null : websiteUrl.trim(),
      description: description.trim() === '' ? null : description,
      lat: toNumOrNull(lat),
      lng: toNumOrNull(lng),
      yearOfCompletion: toIntOrNull(year),
      numberOfUnits: toIntOrNull(numberOfUnits),
      minutesShops: toIntOrNull(distances.minutesShops),
      minutesRestaurant: toIntOrNull(distances.minutesRestaurant),
      minutesPublicTransport: toIntOrNull(distances.minutesPublicTransport),
      minutesSchool: toIntOrNull(distances.minutesSchool),
      minutesHospital: toIntOrNull(distances.minutesHospital),
      minutesSport: toIntOrNull(distances.minutesSport),
    };
    const parsed = ProjectInputSchema.safeParse(candidate);
    if (!parsed.success) {
      const labels = Object.fromEntries(PROJECT_FIELDS.map((field) => [field, t(`form.fields.${field}`)]));
      setIssues(zodIssueList(parsed.error, labels));
      return;
    }
    setIssues([]);
    save.mutate({ id: project.id, project: parsed.data });
  }

  const canSave = name.trim().length > 0;

  return (
    <section className="hp-card">
      <HpCardHeader title={t('form.title')} subtitle={t('form.subtitle')} icon="projects">
        {savedNotice && <HpTag tone="success">{t('form.saved')}</HpTag>}
        <HpButton
          variant="primary"
          icon="check"
          busy={save.isPending}
          disabled={!canSave || !canEdit}
          title={canEdit ? undefined : t('form.needsPermission')}
          onClick={submit}
        >
          {save.isPending ? t('form.saving') : t('form.save')}
        </HpButton>
      </HpCardHeader>
      <div className="hp-card__body bo-sections">
        <FormGrid className="bo-form-grid--3">
          <FormField label={t('form.fields.name')} className="bo-span-2">
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label={t('form.fields.yearOfCompletion')}>
            <input type="number" min={1800} max={2200} value={year} onChange={(e) => setYear(e.target.value)} />
          </FormField>
          <FormField label={t('form.website')} className="bo-span-2">
            <input
              type="url"
              placeholder="https://…"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </FormField>
          <FormField label={t('form.fields.numberOfUnits')} hint={t('form.numberOfUnitsHint')}>
            <input
              type="number"
              min={0}
              max={100000}
              placeholder={t('form.numberOfUnitsPlaceholder', { count: unitCountFallback })}
              value={numberOfUnits}
              onChange={(e) => setNumberOfUnits(e.target.value)}
            />
          </FormField>
          <FormField label={t('form.descriptionMarkdown')} full>
            <textarea rows={6} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
          <FormField label={t('form.fields.address')} full>
            <input value={address} onChange={(e) => setAddress(e.target.value)} />
          </FormField>
        </FormGrid>

        <LocationPicker
          address={address}
          lat={lat}
          lng={lng}
          onChange={(la, ln) => {
            setLat(la);
            setLng(ln);
          }}
        />

        <fieldset className="bo-fieldset">
          <legend>{t('form.distancesLegend')}</legend>
          <div className="bo-distance-grid">
            {DISTANCE_FIELDS.map((key) => (
              <FormField key={key} label={t(`form.distances.${key}`)}>
                <input
                  type="number"
                  min={0}
                  max={6000}
                  value={distances[key]}
                  onChange={(e) => setDistances((d) => ({ ...d, [key]: e.target.value }))}
                />
              </FormField>
            ))}
          </div>
        </fieldset>

        <ValidationErrors issues={issues} />
        <ErrorAlert error={save.error} />
      </div>
    </section>
  );
}
